// Catalog.ID Client — Organization Recovery Kit Pane
// ============================================================================
// The ORGANIZATION's recovery kit, managed from the org workspace while acting
// as a delegate.
//
// Self-contained on purpose. An organization account has no session of its own
// — its password is a never-disclosed throwaway and /api/delegate/identities
// only ever links personal↔delegate — so there is no org identity to switch to
// and no Settings pane that could run these ceremonies "as the organization".
// Create/reissue and the fire drill therefore run right here against the
// org-scoped endpoints, driven by the ORGANIZATION supersecret from its document set.
//
// Owner/admin only: a kit's threshold and custodian count are governance facts,
// so viewers and treasurers get a one-liner and no request is made.

import { createElement, createFieldGroup, renderPaneActions } from '@catalog-shell';
import type {
    PaneActionDescriptor, PaneInstance, PaneType, PaneTypeRenderContext,
} from '@catalog-shell';
import * as organizationApi from '../api/organization';
import type { RecoveryKitInfo } from '../api/account';
import { identity } from '../state/identity';
import { appendRecoveryKitStatus } from '../components/recovery-kit-status';
import { planRecoveryKitActions } from '../components/recovery-kit-actions';
import {
    showRecoveryKitModal as showRecoveryKitCreatorModal,
    showRecoveryReprintModal,
    showRecoveryDrillModal,
} from '../components/recovery-kit-modals';
import { buildOrgKitSource, runOrgRecoveryDrill } from '../components/org-recovery-kit';
import { persistOrgKitEntry } from '../components/recovery-kit-store';
import { PANE_IDS } from '../shell-config';

const ORG_SUPERSECRET_PROMPT =
    'ORGANIZATION supersecret — the one printed in the organization\'s document set, not your own personal or '
    + 'delegate supersecret (verified against the organization key bag, then split across the sheets):';

// ============================================================================
// Title-row actions
//
// Create/reissue and the fire drill are what this pane is FOR, so they ride in
// its title row rather than in the body (shell USAGE, "Where a button belongs").
// They are planned from the kit as last read: the row stays empty until the
// lookup settles, and every path that renders an explanation instead of the
// manage view (no organization in session, viewer/treasurer role) leaves it
// empty for good.
// ============================================================================

/** What the title row needs to know: the org and its kit as last read. */
export interface OrgRecoveryActionState {
    view: { orgCid: string; kit: RecoveryKitInfo | null } | null;
}

interface OrgRecoveryState extends OrgRecoveryActionState {
    ctx: PaneTypeRenderContext;
    /** Re-runs the pane body, which re-reads the kit and re-plans this row. */
    refresh: () => void;
}

const states = new WeakMap<PaneInstance, OrgRecoveryState>();

/**
 * Pure: which ceremonies the row offers, given the kit as last read.
 *
 * The two ceremonies themselves are planned by the shared
 * `planRecoveryKitActions` — Settings offers the same pair for the personal kit
 * — so this function is only the pane's own gate: a state that never reached
 * the manage view offers nothing at all.
 */
export function planOrgRecoveryActions(
    st: OrgRecoveryActionState | undefined,
    exec: (action: 'reprint' | 'create' | 'test') => void,
): PaneActionDescriptor[] {
    if (!st?.view) return [];
    return planRecoveryKitActions(st.view.kit, exec);
}

function orgRecoveryActions(instance: PaneInstance): HTMLElement[] {
    const st = states.get(instance);
    return renderPaneActions(planOrgRecoveryActions(st, (action) => {
        if (!st?.view) return;
        if (action === 'create') {
            showOrgKitModal(st.ctx, st.view.orgCid, Boolean(st.view.kit), st.refresh);
        } else if (action === 'reprint' && st.view.kit) {
            showOrgReprintModal(st.ctx, st.view.orgCid, st.view.kit, st.refresh);
        } else if (action === 'test' && st.view.kit) {
            showOrgDrillModal(st.ctx, st.view.orgCid, st.view.kit, st.refresh);
        }
    }));
}

export function createOrgRecoveryPane(): PaneType {
    return {
        typeId: PANE_IDS.orgRecovery,
        kind: 'leaf',
        defaultTitle: 'Recovery Kit',
        capabilities: [],
        tabBarActions: orgRecoveryActions,
        onDestroy: (instance) => { states.delete(instance); },
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const st: OrgRecoveryState = {
                ctx,
                view: null,
                refresh: () => { void renderOrgRecovery(container, st); },
            };
            states.set(ctx.instance, st);
            void renderOrgRecovery(container, st);
            return container;
        },
    };
}

async function renderOrgRecovery(container: HTMLElement, st: OrgRecoveryState): Promise<void> {
    const ctx = st.ctx;
    container.innerHTML = '';
    // Every early return below leaves the title row empty; the manage view fills
    // it in again once the kit lookup has settled.
    st.view = null;
    const replanActions = (): void => { ctx.shell.refreshPaneActions(ctx.instance.instanceId); };

    const group = createFieldGroup({ title: 'Organization Recovery Kit' });
    container.appendChild(group.element);

    const orgCid = identity.getOrganizationCid();
    if (!orgCid) {
        group.body.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'No organization is active in this session.',
        }));
        replanActions();
        return;
    }

    if (!identity.hasRole('owner') && !identity.hasRole('admin')) {
        group.body.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'Only owners and admins can see the organization\'s recovery-kit status.',
        }));
        replanActions();
        return;
    }

    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'A Recovery Kit splits the organization\'s supersecret across printable sheets using '
            + 'threshold secret sharing: any quorum of distinct sheets reconstructs it, fewer reveal nothing. '
            + 'Sheets are meant to live with different custodians.',
    }));

    let kit: RecoveryKitInfo | null = null;
    let failed = false;
    try {
        kit = await organizationApi.getOrgRecoveryKit(orgCid);
    } catch {
        failed = true;
    }

    if (failed) {
        group.body.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'Could not read the organization\'s recovery-kit status.',
        }));
    } else {
        appendRecoveryKitStatus(group.body, kit, 'No recovery kit on record for this organization.');
    }

    // The ceremonies ride in the title row; the note explaining what they need
    // stays here, next to the status they act on.
    st.view = { orgCid, kit };
    replanActions();

    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Both ceremonies need the ORGANIZATION supersecret — the one printed in the '
            + 'organization\'s document set — or a quorum of its sheets. Your own supersecret will not open '
            + 'the organization\'s keys.',
    }));
}

/** Create/reissue the ORG kit: org key bag, org public keys, org kit record. */
function showOrgKitModal(
    ctx: PaneTypeRenderContext,
    orgCid: string,
    isReissue: boolean,
    onDone: () => void,
): void {
    showRecoveryKitCreatorModal({
        shell: ctx.shell,
        isReissue,
        heading: isReissue ? 'Reissue Organization Recovery Kit' : 'Create Organization Recovery Kit',
        creator: {
            cid: orgCid,
            accountTypeLabel: 'Organization',
            kind: 'organization',
            supersecretPrompt: ORG_SUPERSECRET_PROMPT,
            source: buildOrgKitSource(orgCid),
            // §16.6: the org bag has no single-entry endpoint, so the new
            // generation and the revoked old one go up in ONE step-up-gated
            // whole-bag PUT — no two-active window to repair.
            persistKitEntry: params => persistOrgKitEntry({ orgCid, ...params }),
            recordKit: req => organizationApi.putOrgRecoveryKit(orgCid, req),
            onCreated: () => onDone(),
        },
    });
}

/** Print more sheets of the ORGANIZATION's existing set (§16.6). */
function showOrgReprintModal(
    ctx: PaneTypeRenderContext,
    orgCid: string,
    kit: RecoveryKitInfo,
    onDone: () => void,
): void {
    showRecoveryReprintModal({
        shell: ctx.shell,
        heading: 'Print Organization Recovery Sheets',
        reprint: {
            cid: orgCid,
            accountTypeLabel: 'Organization',
            supersecretPrompt: ORG_SUPERSECRET_PROMPT,
            source: buildOrgKitSource(orgCid),
            record: kit,
            // No org bundle-refresh endpoint exists, and an organization has no
            // bundle file for the record to be stale against (Addendum §14.4),
            // so a record that already names this set is left alone rather than
            // re-PUT — which would reset the organization's fire-drill date.
            recordKit: req => organizationApi.putOrgRecoveryKit(orgCid, req),
            onDone,
        },
    });
}

/** Org fire drill (§14) — no backup file: the quorum is verified against the
 *  organization's key bag on this node. */
function showOrgDrillModal(
    ctx: PaneTypeRenderContext,
    orgCid: string,
    kit: RecoveryKitInfo,
    onDone: () => void,
): void {
    showRecoveryDrillModal({
        shell: ctx.shell,
        heading: 'Test Organization Recovery Kit',
        intro: `Kit ${kit.recoverySetId.slice(0, 8)}… (${kit.thresholdK} of ${kit.totalSharesN}). `
            + `Scan ${kit.thresholdK} distinct sheets: the test reconstructs the organization supersecret in `
            + 'memory only and verifies it against the organization\'s key bag held on this node, so no '
            + 'backup file is needed — nothing is stored or sent anywhere.',
        run: async (cards) => {
            const message = await runOrgRecoveryDrill(orgCid, cards);
            onDone();
            return message;
        },
    });
}
